import { useId, useState } from 'react';
import { useLocationStore } from '@/stores/useLocationStore';
import { useCartStore } from '@/stores/useCartStore';
import { DateRange } from '@/types';
import { addDays, format, startOfDay } from 'date-fns';
import { ContextChangeWarningModal } from './ContextChangeWarningModal';

interface DateSelectContentProps {
    onClose?: () => void;
    deferChanges?: boolean;
}

export const DateSelectContent = ({ onClose, deferChanges = false }: DateSelectContentProps) => {
    const dateFieldId = useId();
    const { location, dateRange: savedDateRange, setDateRange } = useLocationStore();
    // A picker inside a product card must not refresh/unmount its own card
    // until the customer has finished choosing both dates.
    const [draftDateRange, setDraftDateRange] = useState(savedDateRange);
    const dateRange = deferChanges ? draftDateRange : savedDateRange;
    const cartItems = useCartStore((state) => state.items);
    const itemCount = useCartStore((state) => state.itemCount());
    const validateCart = useCartStore((state) => state.validateCart);

    // Warning modal state
    const [showWarningModal, setShowWarningModal] = useState(false);
    const [pendingDateRange, setPendingDateRange] = useState<DateRange | null>(null);

    const formatDateRange = (range: DateRange | null) => {
        if (!range?.from) return undefined;
        const fromStr = format(range.from, 'MMM d');
        const toStr = range.to ? format(range.to, 'MMM d') : '';
        return toStr ? `${fromStr} - ${toStr}` : fromStr;
    };

    const applyDateChange = (newRange: DateRange) => {
        // Check if dates actually changed
        const datesChanged =
            newRange.from?.getTime() !== savedDateRange.from?.getTime() ||
            newRange.to?.getTime() !== savedDateRange.to?.getTime();

        if (cartItems.length > 0 && datesChanged) {
            // Show warning modal
            setPendingDateRange(newRange);
            setShowWarningModal(true);
            return false;
        } else {
            setDateRange(newRange);
            return true;
        }
    };

    const handleDateChange = (newRange: DateRange) => {
        if (deferChanges) setDraftDateRange(newRange);
        else applyDateChange(newRange);
    };

    const handleConfirmDateChange = async () => {
        if (pendingDateRange) {
            setDateRange(pendingDateRange);

            // Trigger cart validation if we have location and both dates set
            if (location?.id && pendingDateRange.from && pendingDateRange.to && cartItems.length > 0) {
                const startDate = format(pendingDateRange.from, 'yyyy-MM-dd');
                const endDate = format(pendingDateRange.to, 'yyyy-MM-dd');
                await validateCart(startDate, endDate, location.id);
            }

            onClose?.();
        }
        setPendingDateRange(null);
    };

    const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        // Use local noon (no Z suffix) so .toISOString() stays on the same calendar date in any timezone
        const date = e.target.value ? new Date(`${e.target.value}T12:00:00`) : undefined;
        handleDateChange({ ...dateRange, from: date });
    };

    const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const date = e.target.value ? new Date(`${e.target.value}T12:00:00`) : undefined;
        handleDateChange({ ...dateRange, to: date });
    };

    const hasDates = !!(dateRange.from || dateRange.to);
    const hasValidDates = !!(dateRange.from && dateRange.to && dateRange.to > dateRange.from && dateRange.from >= startOfDay(new Date()));

    const handleClearDates = () => {
        if (applyDateChange({ from: undefined, to: undefined })) onClose?.();
    };

    return (
        <>
            <div className="space-y-4 p-4">
                <div className="grid gap-2">
                    <label htmlFor={`${dateFieldId}-start`} className="text-sm font-medium text-foreground">Start Date</label>
                    <input
                        id={`${dateFieldId}-start`}
                        type="date"
                        className="w-full rounded-md border border-input px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-ring"
                        min={new Date().toISOString().split('T')[0]}
                        onChange={handleStartDateChange}
                        value={dateRange.from ? dateRange.from.toISOString().split('T')[0] : ''}
                    />
                </div>
                <div className="grid gap-2">
                    <label htmlFor={`${dateFieldId}-end`} className="text-sm font-medium text-foreground">End Date</label>
                    <input
                        id={`${dateFieldId}-end`}
                        type="date"
                        className="w-full rounded-md border border-input px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-ring"
                        min={dateRange.from ? addDays(dateRange.from, 1).toISOString().split('T')[0] : new Date().toISOString().split('T')[0]}
                        onChange={handleEndDateChange}
                        value={dateRange.to ? dateRange.to.toISOString().split('T')[0] : ''}
                    />
                </div>

                {deferChanges && (
                    <button
                        type="button"
                        disabled={!hasValidDates}
                        onClick={() => {
                            if (hasValidDates && applyDateChange(draftDateRange)) onClose?.();
                        }}
                        className="min-h-[44px] w-full rounded-md bg-primary px-3 py-2 text-sm font-semibold text-primary-foreground disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        Apply dates
                    </button>
                )}

                {hasDates ? (
                    <button
                        type="button"
                        onClick={handleClearDates}
                        className="w-full rounded-md border border-border/60 bg-secondary/60 px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
                    >
                        Clear dates
                    </button>
                ) : (
                    <button
                        type="button"
                        onClick={() => onClose?.()}
                        className="w-full rounded-md px-3 py-2 text-sm text-muted-foreground/70 transition-colors hover:text-muted-foreground"
                    >
                        I&apos;ll decide later
                    </button>
                )}
            </div>

            {/* Warning Modal */}
            <ContextChangeWarningModal
                isOpen={showWarningModal}
                onClose={() => {
                    setShowWarningModal(false);
                    setPendingDateRange(null);
                }}
                onConfirm={handleConfirmDateChange}
                changeType="dates"
                itemCount={itemCount}
                currentDates={formatDateRange(savedDateRange)}
                newDates={formatDateRange(pendingDateRange)}
            />
        </>
    );
};
